import {
  createBundle,
  updateBundle,
  setBundleStatus,
  deleteBundle,
  getStructure,
  getBundleCompanies,
  getBundleListing,
  getBundle,
} from "./bundles.action";
import { addAlert, setLoading } from "../../notification";

// The api layer rejects with the SERVER'S message, so a rejected create/update carries the
// real reason in action.payload — "A bundle with the same modules already exists: ..." or
// "Bundle name already exists". Do not replace it with a guess.
const failureMessage = (action, fallback) => action.payload || fallback;

// Creating a bundle is an outcome in its own right on Access Bundles > Add. It is NOT an
// outcome on the company/approval surfaces: there the create is the first half of "put this
// company on these modules", and the thing the admin wants to hear about is the assign. Two
// toasts for one Save is one too many, and because the snackbar is a QUEUE the green one
// would be shown first, for its full 3.5s, even when the assign that followed it failed —
// so the admin briefly sees nothing but success for a save that did not happen.
//
// resolveBundleId therefore marks its create as an interim step. The REJECTED case below is
// never suppressed: a failed create is the whole outcome of that save, since the caller
// short-circuits and nothing is assigned.
const isSuccessAlertSuppressed = (action) => Boolean(
  action.meta && action.meta.arg && action.meta.arg.suppressSuccessAlert
);

const createMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case createBundle.pending.type:
      dispatch(setLoading({ id: createBundle.typePrefix, state: true }));
      break;

    case createBundle.fulfilled.type:
      dispatch(setLoading({ id: createBundle.typePrefix, state: false }));
      if (!isSuccessAlertSuppressed(action)) {
        dispatch(
          addAlert({
            severity: "success",
            message: "Bundle created successfully",
          })
        );
      }
      break;

    case createBundle.rejected.type:
      dispatch(setLoading({ id: createBundle.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "error",
          message: failureMessage(action, "Failed to create bundle"),
        })
      );
      break;

    default:
      break;
  }
};

const updateMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case updateBundle.pending.type:
      dispatch(setLoading({ id: updateBundle.typePrefix, state: true }));
      break;

    case updateBundle.fulfilled.type:
      dispatch(setLoading({ id: updateBundle.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "success",
          message: "Bundle updated successfully",
        })
      );
      break;

    case updateBundle.rejected.type:
      dispatch(setLoading({ id: updateBundle.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "error",
          message: failureMessage(action, "Failed to update bundle"),
        })
      );
      break;

    default:
      break;
  }
};

const setStatusMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case setBundleStatus.pending.type:
      dispatch(setLoading({ id: setBundleStatus.typePrefix, state: true }));
      break;

    case setBundleStatus.fulfilled.type:
      dispatch(setLoading({ id: setBundleStatus.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "success",
          message: "Bundle status updated successfully",
        })
      );
      break;

    case setBundleStatus.rejected.type:
      dispatch(setLoading({ id: setBundleStatus.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "error",
          message: failureMessage(action, "Failed to update bundle status"),
        })
      );
      break;

    default:
      break;
  }
};

const deleteMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case deleteBundle.pending.type:
      dispatch(setLoading({ id: deleteBundle.typePrefix, state: true }));
      break;

    case deleteBundle.fulfilled.type:
      dispatch(setLoading({ id: deleteBundle.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "success",
          message: "Bundle deleted successfully",
        })
      );
      break;

    case deleteBundle.rejected.type:
      dispatch(setLoading({ id: deleteBundle.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "error",
          message: failureMessage(action, "Failed to delete bundle"),
        })
      );
      break;

    default:
      break;
  }
};

const getStructureMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case getStructure.pending.type:
      dispatch(setLoading({ id: getStructure.typePrefix, state: true }));
      break;

    case getStructure.fulfilled.type:
      dispatch(setLoading({ id: getStructure.typePrefix, state: false }));
      break;

    case getStructure.rejected.type:
      dispatch(setLoading({ id: getStructure.typePrefix, state: false }));
      dispatch(
        addAlert({
          severity: "error",
          message: "Failed to fetch bundle structure",
        })
      );
      break;

    default:
      break;
  }
};

// The Edit dialog's REPLACE-confirmation gate is fail-closed on this count: Save is disabled
// and an unconfirmed save is refused while it is unknown (#5). That only works if the count's
// in-flight state is observable — so its loading flag MUST be tracked the same way every other
// mutation is. Without this the flag stays false for the whole fetch, the slice's pending()
// reducer having already stamped the bundle id with total 0, so the gate reads "known, count 0"
// and lets a save through before the real count lands. Kept as its own middleware (no success
// toast — a read has no outcome to announce) alongside the writes.
const getBundleCompaniesMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  const { type } = action;

  switch (type) {
    case getBundleCompanies.pending.type:
      dispatch(setLoading({ id: getBundleCompanies.typePrefix, state: true }));
      break;

    case getBundleCompanies.fulfilled.type:
      dispatch(setLoading({ id: getBundleCompanies.typePrefix, state: false }));
      break;

    case getBundleCompanies.rejected.type:
      dispatch(setLoading({ id: getBundleCompanies.typePrefix, state: false }));
      break;

    default:
      break;
  }
};

// The two reads that draw the Access Bundles surfaces. A failed read is a per-thunk alert
// (the blanket 401/403 interceptor was dropped in favour of these, so nothing else announces
// it): the listing feeds the table — which also raises an inline Retry panel, so the toast is
// the transient notice and the panel the recovery affordance — and getBundle feeds the Edit
// dialog, which has no inline surface, so the toast is its only reason-why. Both carry the
// server's message through failureMessage rather than a guess.
const getBundleListingMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  if (action.type === getBundleListing.rejected.type) {
    dispatch(
      addAlert({
        severity: "error",
        message: failureMessage(action, "Failed to load bundles"),
      })
    );
  }
};

const getBundleMiddleware = ({ dispatch }) => (next) => async (action) => {
  next(action);

  if (action.type === getBundle.rejected.type) {
    dispatch(
      addAlert({
        severity: "error",
        message: failureMessage(action, "Failed to load bundle"),
      })
    );
  }
};

const middlewares = [
  createMiddleware,
  updateMiddleware,
  setStatusMiddleware,
  deleteMiddleware,
  getStructureMiddleware,
  getBundleCompaniesMiddleware,
  getBundleListingMiddleware,
  getBundleMiddleware,
];

export default middlewares;
